import React from 'react';
import styled, { keyframes } from 'styled-components';
import OffscreenText from '@zocdoc-frontend-common/components-offscreen-text';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';

const animation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const Container = styled.div`
    align-items: center;
    background-color: ${(p) =>
        p.inline ? 'none' : p.theme.components.Spinner.maskColor};
    display: ${(p) => (p.inline ? 'inline-flex' : 'flex')};
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    position: ${(p) => (p.inline ? 'relative' : 'absolute')};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
`;

const BaseSpinner = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    position: relative;
    height: ${(p) => p.size}px;
    width: ${(p) => p.size}px;

    &::before {
        content: '';
        animation: ${animation} 1s linear infinite;
        border: ${(p) => p.size / 8}px solid
            ${(p) => p.theme.components.Spinner.trackColor};
        border-top-color: ${(p) => p.theme.components.Spinner.segmentColor};
        border-radius: 50%;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
`;

const VisibleLoadingText = styled.div`
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: ${(p) => p.size / 7}px;
    line-height: 1.3;
    margin-top: 20px;
    text-align: center;
`;

/**
 * @param {boolean} inline
 * @param {string} loadingText
 * @param {boolean} showLoadingText
 * @param {number} size - unit is pixels
 * @param {Object} props
 * @return {JSX.Element}
 */
function Spinner({
    inline = false,
    loadingText = 'Loading...',
    showLoadingText = false,
    size = 120,
    ...props
}) {
    const LoadingContainer = showLoadingText
        ? VisibleLoadingText
        : OffscreenText;

    return (
        <Container {...props} inline={inline}>
            <BaseSpinner size={size} />
            <LoadingContainer size={showLoadingText ? size : undefined}>
                {loadingText}
            </LoadingContainer>
        </Container>
    );
}

export default Spinner;
